import { useState, useEffect, useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Form, message } from 'antd';

import { useAsync, useCheckPermissions, useGlobalModal } from 'hooks';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useMutation, useGetDataQueryConditions } from 'hooks';
import { updateIdQuery } from 'utils/queryCondition';
import { useDefaultSearchText } from 'pages/home/utils';

import { RIBBON_TYPES } from 'static/Constants';
import { saveQuery } from 'services/query';

import { selectRibbonType } from '@redux/ribbon/selectors';
import selectorsGridView from 'common/components/grid-view/controllers/selectors';

import { handleCreateSearchContainer } from 'pages/reporting/utils';
import { getGridName } from 'utils/getGridName';
import { filterParamsColumns } from 'pages/home/ribbon/components/controls/home/grid-query/utils';
import { deleteColumnCheckbox } from './utils';

import {
  getQueryGridView,
  getQueryDetailPane,
  getAllMemberRoles,
} from 'services/query';
import { getMemberList } from 'services/members';

import { PERMISSION_MANAGE_CURATED_QUERY } from 'static/Permission';
import messagesGridView from 'i18n/messages/gridView';

const useSetDefaultWidth = (form, columns, selectedFieldName) => {
  const valueColumns = deleteColumnCheckbox(columns);

  useEffect(() => {
    if (valueColumns.length === 0) return;

    const findColumn = valueColumns.find(
      (col) => col.fieldName === selectedFieldName
    );
    const width = findColumn?.width ?? 0;

    form.setFieldsValue({ width });
  }, [form, valueColumns, selectedFieldName]);
};

const useChangeWidth = (form, width) => {
  useEffect(() => {
    form.setFieldsValue({ width });
  }, [form, width]);
};

const useGetSavedConfig = (gridName, isMounted) => {
  const [data, setData] = useState();

  useEffect(() => {
    const params = {
      pageIndex: 1,
      pageSize: 10,
      sort: [
        {
          fieldName: 'lastModified',
          isAscending: false,
        },
      ],
      gridName,
    };
    const delayFetchGrid = setTimeout(() => {
      getQueryGridView(params).then((res) => {
        setData(res.data);
      });
    }, 1000);

    return () => clearTimeout(delayFetchGrid);
  }, [gridName, isMounted]);

  return data;
};

const DEFAULT_PAGE_SIZE = 9999;

const useGetMembers = ({ valuesFilter, enabled }) => {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!enabled) return;

    const params = {
      type: 'GET_MEMBER_LIST',
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      sort: [],
      filters: [
        {
          fieldName: 'id',
          values: valuesFilter,
        },
      ],
    };

    getMemberList(params).then((resp) => {
      setMembers(resp.gridData);
    });
  }, [enabled, valuesFilter]);

  return members;
};

const useGetCuratedQueryDetail = ({ curatedQueryId, enabled }) => {
  const { data, run } = useAsync();

  useEffect(() => {
    if (!enabled) return;

    run(getQueryDetailPane({ id: curatedQueryId }));
  }, [curatedQueryId, enabled, run]);

  const refetchGetCuratedQueryDetail = useCallback(() => {
    run(getQueryDetailPane({ id: curatedQueryId }));
  }, [curatedQueryId, run]);

  return {
    curatedQueryData: data ?? {},
    refetchGetCuratedQueryDetail,
  };
};

const getAllMemberRolesQueryKey = () => {
  return ['get-all-member-role'];
};

const useGetAllMemberRoles = () => {
  const hasPermissionManageCuratedQuery = useCheckPermissions(
    PERMISSION_MANAGE_CURATED_QUERY
  );

  const { data, ...restData } = useQuery({
    queryKey: getAllMemberRolesQueryKey(),
    queryFn: async () => {
      const response = await getAllMemberRoles();

      return response?.data;
    },
    enabled: hasPermissionManageCuratedQuery,
  });

  return {
    memberRoles: data?.memberRoles ?? [],
    ...restData,
  };
};

const useCreateQueryForm = () => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const dispatch = useDispatch();

  // const [visible, setVisible] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [toggleCuratedQuery, setCuratedQuery] = useState(false);
  const [shareType, setShareType] = useState('MemberRole');

  const { MODAL_NAMES, closeModal, checkModalVisible } = useGlobalModal();
  const visible = checkModalVisible(MODAL_NAMES.CREATE_QUERY);

  const [
    { status: statusCreate, data: dataCreate },
    createConfig,
    setDefaultState,
  ] = useMutation(saveQuery);
  const { getDefaultSearchText, getDefaultPrimaryFieldsOnly } =
    useDefaultSearchText();

  const { memberRoles } = useGetAllMemberRoles();

  const { chosenColumns } = useSelector(
    selectorsGridView.makeSelectGridConfig()
  );
  const queryConditions = useGetDataQueryConditions(window.location.pathname);
  const ribbonTypes = useSelector(selectRibbonType());
  const gridName = getGridName(window.location.pathname);
  const searchText = getDefaultSearchText(window.location.pathname);
  const primaryFieldsOnly = getDefaultPrimaryFieldsOnly(
    window.location.pathname
  );

  const searchCategory = useSelector(
    selectorsGridView.makeSelectSearchCategoriesList()
  );

  useEffect(() => {
    form.setFieldsValue({ gridName });
  }, [form, gridName]);

  useEffect(() => {
    form.resetFields();
    setSelectedMembers([]);
    setCuratedQuery(false);
    setShareType('MemberRole');
  }, [form, visible]);

  useEffect(() => {
    if (statusCreate === 'success') {
      message.success(intl.formatMessage(messagesGridView.createConfigSuccess));

      updateIdQuery(dispatch, dataCreate.id, window.location.pathname);
    }

    if (statusCreate === 'error') {
      message.error(intl.formatMessage(messagesGridView.createConfigError));
    }

    form.resetFields();
    closeModal(MODAL_NAMES.CREATE_QUERY);
    setDefaultState();
  }, [dispatch, form, gridName, intl, statusCreate]);

  const handleCreateConfig = async () => {
    const { isDefault, isCuratedQuery, dateActive, ...values } =
      await form.validateFields();

    const advancedSearchContainer =
      handleCreateSearchContainer(queryConditions);

    let params = {
      ...values,
      gridName,
    };

    // Add params curated query
    if (isCuratedQuery) {
      const { curatedQueryShareType, curatedQueryShareToMemberRoleId } = values;

      params = { ...params, isCuratedQuery };

      if (curatedQueryShareType === 'MemberRole') {
        if (curatedQueryShareToMemberRoleId) {
          params = {
            ...params,
            curatedQueryShareToMemberRoleId,
            curatedQueryShareToMemberType:
              memberRoles.find(
                (role) => role.memberRoleId === curatedQueryShareToMemberRoleId
              )?.memberRoleName ?? null,
          };
        }
        params = {
          ...params,
          curatedQueryShareType,
          curatedQueryShareToMembers: [],
        };
      }

      if (curatedQueryShareType === 'SpecificMembers') {
        params = {
          ...params,
          curatedQueryShareType,
          curatedQueryShareToMembers: selectedMembers.map(
            (member) => member.id
          ),
        };
      }

      if (curatedQueryShareType === 'AllMember') {
        params = {
          ...params,
          curatedQueryShareType,
          curatedQueryShareToMemberType: null,
          curatedQueryShareToMembers: [],
        };
      }

      if (dateActive) {
        params = {
          ...params,
          effectedDate: dateActive[0].toISOString(),
          expirationDate: dateActive[1].toISOString(),
        };
      } else {
        params = {
          ...params,
          effectedDate: null,
          expirationDate: null,
        };
      }
    }

    let jsonConfig;
    let downloadJsonFormat;
    if (ribbonTypes === RIBBON_TYPES.DETAILSVIEW) {
      jsonConfig = JSON.stringify({
        columns: filterParamsColumns(chosenColumns),
        queryConditions: queryConditions,
        ribbonTypes: ribbonTypes,
        urlForwardTo: window.location.pathname,
        searchText,
        'search.searchText': searchText,
        primaryFieldsOnly,
        'search.primaryFieldsOnly': primaryFieldsOnly,
      });
    } else {
      jsonConfig = JSON.stringify({
        queryConditions: queryConditions,
        ribbonTypes: ribbonTypes,
        urlForwardTo: window.location.pathname,
        searchText,
        'search.searchText': searchText,
        primaryFieldsOnly,
        'search.primaryFieldsOnly': primaryFieldsOnly,
      });
    }
    downloadJsonFormat = JSON.stringify({
      advancedSearchContainer,
      search: {
        searchText,
        primaryFieldsOnly,
      },
      isFavoriteRoute: false,
      fromDate: null,
      searchCategory,
      filters: [],
      pageSize: 20,
      pageIndex: 1,
    });
    params = Object.assign(params, {
      query: jsonConfig,
      downloadJsonFormat: downloadJsonFormat,
    });

    createConfig(params);
    closeModal(MODAL_NAMES.CREATE_QUERY);
  };
  return {
    toggleCuratedQuery,
    shareType,
    setShareType,
    handleCreateConfig,
    form,
    selectedMembers,
    setSelectedMembers,
    setCuratedQuery,
    gridName,
    memberRoles,
    statusCreate,
  };
};

export {
  useSetDefaultWidth,
  useChangeWidth,
  useGetSavedConfig,
  useGetMembers,
  useGetCuratedQueryDetail,
  useGetAllMemberRoles,
  useCreateQueryForm,
};
